export type SapConnector = "sap-ecc" | "sap-s4hana"

export const SAP_CONNECTORS = {
  "sap-ecc": { name: "SAP ECC RFC", description: "Your SAP ERP account — RFCs, ABAP development and enabled business APIs." },
  "sap-s4hana": { name: "SAP S/4HANA", description: "Your on-premise or private-cloud SAP account — ABAP and enabled business APIs." },
} satisfies Record<SapConnector, { name: string; description: string }>

export function connectionFeatureEnabled(flags: Record<string, boolean> | undefined, flag: string): boolean {
  return flags?.[flag] !== false
}

export type SapConfig = {
  system: string
  client: string
  language: string
  environment: "DEV" | "QAS" | "PRD"
  edition: "on-premise" | "private"
  release?: string
  adtTransport: "https" | "open-rfc" | "disabled"
  adtUrl?: string
  adtAddress?: string
  rfc?: { host: string; systemNumber: string; port: number; address?: string }
  businessUrl?: string
  businessAddress?: string
  accessPolicy: "READ_ONLY" | "WRITES_WITH_APPROVAL" | "BLOCKED"
  allowDebug: boolean
  allowCodeExecution: boolean
  allowProductionWrites: boolean
  allowReviewedRfcCalls: boolean
  openRfcAdtMode: "read-only" | "development"
}

export type SapSecret = "username" | "password" | "businessUsername" | "businessPassword" | "businessToken"
export type SapConnection = {
  id: string
  connector: SapConnector
  label: string
  active: boolean
  revision: number
  vpnProfileId?: string | null
  config: SapConfig
  policy: Record<string, unknown>
  capabilities: Record<string, unknown> | null
  secrets: Partial<Record<SapSecret, string>>
  createdAt: string
  updatedAt: string
}

export const SAP_EMPTY_POLICY = {
  rfcs: {}, tables: {}, queries: {}, editableObjects: [], editablePackages: [], businessOperations: {},
  transports: { actions: [], requests: [] },
}

export const SAP_DEFAULT_CONFIG: SapConfig = {
  system: "", client: "100", language: "ES", environment: "QAS", edition: "on-premise", adtTransport: "https",
  accessPolicy: "READ_ONLY", allowDebug: false, allowCodeExecution: false,
  allowProductionWrites: false, allowReviewedRfcCalls: false, openRfcAdtMode: "read-only",
}

/** Form values are explicit: unchecked controls must disable previously enabled
 * capabilities, while an unchanged masked secret must never become a credential. */
export function sapConnectionBody(form: FormData) {
  const text = (key: string) => String(form.get(key) ?? "").trim()
  const on = (key: string) => form.get(key) === "on"
  const optional = (key: string) => text(key) || undefined
  const address = (key: string, label: string) => {
    const value = optional(key)
    if (value && (!/^(0|[1-9]\d{0,2})(\.(0|[1-9]\d{0,2})){3}$/.test(value) || value.split(".").some((part) => Number(part) > 255) || !/^(10\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.)/.test(value))) {
      throw new Error(`${label} must be a private IPv4 address, for example 192.168.150.193.`)
    }
    return value
  }
  const policyText = text("policy")
  let policy: unknown
  try { policy = JSON.parse(policyText) } catch { throw new Error("Policy must contain valid JSON.") }
  if (!policy || typeof policy !== "object" || Array.isArray(policy)) throw new Error("Policy must be a JSON object.")
  const accessPolicy = text("accessPolicy")
  const environment = text("environment")
  const adtTransport = text("adtTransport")
  const edition = text("edition") || "on-premise"
  if (!["READ_ONLY", "WRITES_WITH_APPROVAL", "BLOCKED"].includes(accessPolicy)) throw new Error("Select an access policy.")
  if (!["DEV", "QAS", "PRD"].includes(environment)) throw new Error("Select a SAP environment.")
  if (!["https", "open-rfc", "disabled"].includes(adtTransport)) throw new Error("Select an ABAP connection method.")
  if (!["on-premise", "private"].includes(edition)) throw new Error("Select an on-premise or private-cloud edition.")
  if (!text("label") || !text("system")) throw new Error("Enter a label and SAP system ID.")
  if (!/^[A-Za-z0-9]{3}$/.test(text("system"))) throw new Error("SAP system ID must contain three letters or digits.")
  if (!/^\d{3}$/.test(text("client"))) throw new Error("SAP client must contain three digits.")
  if (!/^[A-Za-z]{1,2}$/.test(text("language"))) throw new Error("Use a SAP language key, for example ES or EN.")
  const config: SapConfig = {
    system: text("system").toUpperCase(), client: text("client"), language: text("language").toUpperCase(),
    environment: environment as SapConfig["environment"], release: optional("release"),
    edition: edition as SapConfig["edition"],
    adtTransport: adtTransport as SapConfig["adtTransport"], adtUrl: optional("adtUrl"),
    adtAddress: address("adtAddress", "ADT reachable address"), businessUrl: optional("businessUrl"),
    businessAddress: address("businessAddress", "Business API reachable address"),
    accessPolicy: accessPolicy as SapConfig["accessPolicy"], allowDebug: on("allowDebug"),
    allowCodeExecution: on("allowCodeExecution"), allowProductionWrites: on("allowProductionWrites"),
    allowReviewedRfcCalls: on("allowReviewedRfcCalls"),
    openRfcAdtMode: on("openRfcDevelopment") ? "development" : "read-only",
  }
  if (adtTransport === "https" && !config.adtUrl) throw new Error("Enter the ADT HTTPS server URL.")
  if (config.adtAddress && !config.adtUrl) throw new Error("An ADT reachable address needs its HTTPS server URL.")
  if (config.businessAddress && !config.businessUrl) throw new Error("A business API reachable address needs its server URL.")
  if (on("nativeRfc") || adtTransport === "open-rfc") {
    const systemNumber = text("rfcSystemNumber")
    if (!/^\d{2}$/.test(systemNumber)) throw new Error("RFC system number must contain two digits.")
    const port = text("rfcPort") ? Number(text("rfcPort")) : 3300 + Number(systemNumber)
    if (!Number.isInteger(port) || port < 1 || port > 65535) throw new Error("Enter a valid RFC gateway port.")
    if (!text("rfcHost")) throw new Error("Enter the RFC application server.")
    config.rfc = { host: text("rfcHost"), systemNumber, port, address: address("rfcAddress", "RFC reachable address") }
  }
  const secrets: Partial<Record<SapSecret, string>> = {}
  for (const key of ["username", "password", "businessUsername", "businessPassword", "businessToken"] as const) {
    const value = String(form.get(key) ?? "")
    if (value !== "") secrets[key] = value
  }
  return { label: text("label"), active: on("active"), vpnProfileId: text("vpnProfileId") || null, config, policy: policy as Record<string, unknown>, secrets }
}

export function companyConnectionsForTile<T extends { platform?: string }>(connections: T[], tile: string): T[] {
  return connections.filter((connection) => connection.platform === (tile === "sap" ? "sap-commerce" : tile))
}

export function hasSapReadCheck(capabilities: SapConnection["capabilities"]): boolean {
  return typeof capabilities?.checkedAt === "string" && capabilities.checkedAt.length > 0
}

/** Preserve unknown and unsupported states from the server; a saved destination
 * or enabled setting is never evidence of SAP support. */
export function sapCapabilityRows(value: unknown, prefix = ""): { name: string; value: string }[] {
  if (value === null || value === undefined) return [{ name: prefix || "Status", value: "Not tested" }]
  if (Array.isArray(value)) return value.length
    ? value.flatMap((item, index) => sapCapabilityRows(item, `${prefix} / ${index + 1}`))
    : [{ name: prefix || "Status", value: "No entries" }]
  if (typeof value !== "object") return [{ name: prefix || "Status", value: String(value) }]
  const entries = Object.entries(value)
  if (!entries.length) return [{ name: prefix || "Status", value: "Not tested" }]
  return entries.flatMap(([key, item]) => sapCapabilityRows(item, prefix ? `${prefix} / ${key}` : key))
}
